import React, { useState } from "react"
import { ComposableMap, Geographies, Geography } from "react-simple-maps"
import { GeoInsightsProps } from "../../../../../types/dashboard.types"
import chroma from 'chroma-js'

const US_TOPO_JSON =
    "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json"

const COLOR_SCALE = {
    darkest: "#41447D",
    lightest: "#D1D3EE",
    noData: "#F1F1F1"
}

const GeoInsights = ({ mapData, colorMode }: GeoInsightsProps) => {
    const [hoverInfo, setHoverInfo] = useState<null | { x: number; y: number; state: string; total: number; cities: any[] }>(null)
    const [selectedState, setSelectedState] = useState<string | null>(null)
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)
    function getStateNameFromGeo(geo: any) {
        const p = geo.properties || {}
        return p.name || p.NAME || p.state || p.STATE || p.stusps || p.STUSPS || p.postal || p.Postal || null
    }

    const totals: number[] = Object.values(mapData || {}).map((d: any) => Number(d?.total || 0))
    const maxTotal = totals.length ? Math.max(...totals) : 0
    const minTotal = totals.length ? Math.min(...totals) : 0
    const scale = chroma.scale([COLOR_SCALE.lightest, COLOR_SCALE.darkest]).domain([Math.max(0, minTotal), Math.max(1, maxTotal)])

    const getColorForValue = (v?: number) => {
        if (!v || v <= 0) return COLOR_SCALE.noData
        // Clamp value between domain
        const clamped = Math.min(Math.max(v, minTotal), maxTotal)
        return scale(clamped).hex()
    }

    return (
        <>
            <ComposableMap
                projection='geoAlbersUsa'
                projectionConfig={{ scale: 700 }}
                width={650}
                height={350}
                style={{ width: '100%', height: '100%' }}
            >
                <Geographies geography={US_TOPO_JSON}>
                    {(geoObj: { geographies: any[] }) =>
                        geoObj.geographies.map((geo: any) => {
                            const stateName = getStateNameFromGeo(geo)
                            const data = stateName && mapData ? mapData[stateName] : undefined
                            const value = data?.total
                            const hasData = !!value && value > 0
                            const gKey =
                                geo.rsmKey ??
                                geo.id ??
                                geo.properties?.NAME ??
                                JSON.stringify(geo)

                            const baseFill = hasData
                                ? getColorForValue(value)
                                : COLOR_SCALE.noData

                            const isHovered = hoveredKey === gKey

                            const fill = isHovered && hasData
                                ? COLOR_SCALE.darkest
                                : baseFill

                            return (
                                <Geography
                                    key={gKey}
                                    geography={geo}
                                    fill={fill}
                                    fillOpacity={1}
                                    stroke="#FFFFFF"
                                    strokeOpacity={colorMode === 'light' ? 1 : 0.1}
                                    strokeWidth={0.6}
                                    onMouseEnter={(ev: any) => {
                                        setHoveredKey(gKey)
                                        if (!data) return
                                        const x = ev.clientX
                                        const y = ev.clientY
                                        setHoverInfo({ x, y, state: stateName, total: data.total, cities: data.cities })
                                    }}
                                    onMouseMove={(ev: any) => {
                                        if (!data) return
                                        setHoverInfo((h) => (h ? { ...h, x: ev.clientX, y: ev.clientY } : h))
                                    }}
                                    onMouseLeave={() => {
                                        setHoverInfo(null)
                                        setHoveredKey(null)
                                    }}
                                    onClick={() => {
                                        if (data) setSelectedState(stateName)
                                    }}
                                    style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                                />
                            )
                        })
                    }
                </Geographies>

            </ComposableMap>
            {/* Tooltip */}
            {hoverInfo && (
                <div
                    style={{
                        position: 'fixed',
                        left: hoverInfo.x + 12,
                        top: hoverInfo.y + 12,
                        background: 'rgba(255,255,255,0.98)',
                        padding: 10,
                        borderRadius: 6,
                        boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                        pointerEvents: 'none',
                        zIndex: 9999,
                        minWidth: 200,
                    }}
                >
                    <strong>{hoverInfo.state}: {hoverInfo.total} projects</strong>
                    <div style={{ marginTop: 6 }}>
                        {hoverInfo.cities.map((c: any) => (
                            <div key={c.name} style={{ fontSize: 13 }}>{c.name}: {c.count}</div>
                        ))}
                    </div>
                </div>
            )}


        </>
    )
}
export default React.memo(GeoInsights)